import React, { useEffect, useState } from "react";
import { getMatches } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { SectionTitle, EmptyState } from "../components/UIKit";
import { PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function MatchesPage() {
  const { selected, countries } = useCountry();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const country = countries.find((c) => c.code === selected);

  useEffect(() => {
    setLoading(true);
    getMatches(selected)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle
        overline={`${country?.flag || ""} ${country?.name || ""} MATCHES`}
        title="Sell Your List"
      />

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : !data || (!data.builders?.length && !data.associations?.length) ? (
        <EmptyState
          icon={<PaperPlaneTilt size={22} weight="bold" />}
          title="Not enough data to match yet"
          hint={`Run both an Installer scan and a Buyer scan for ${country?.name || "this country"}, then come back here to see the pitch angle for each buyer.`}
        />
      ) : !data.builders?.length ? (
        <EmptyState title="No installers yet" hint="Go to Installers and run a scan." />
      ) : !data.associations?.length ? (
        <EmptyState title="No buyers yet" hint="Go to Buyers and run a scan." />
      ) : (
        <>
          <div className="border border-black bg-[#0A0A0A] text-[#D4FF00] p-4 mb-6" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            <div className="text-xs tracking-[0.2em] uppercase mb-2">MATCH SUMMARY</div>
            <div className="text-2xl tabular-nums leading-none">
              {data.builders.length} installers × {data.associations.length} buyers = <span className="text-white">{data.builders.length * data.associations.length}</span> possible deals in {country?.name}
            </div>
          </div>

          <div className="border border-black bg-white">
            {data.associations.map((a, idx) => (
              <div
                key={a.id}
                data-testid={`match-row-${a.id}`}
                className={`grid grid-cols-1 md:grid-cols-2 ${idx > 0 ? "border-t border-black" : ""}`}
              >
                <div className="p-5 md:border-r border-black/10">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#0047FF] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    BUYER · {(a.type || "other").replace("_", " ")}
                  </div>
                  <div className="text-lg leading-tight tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                    {a.name}
                  </div>
                  <div className="mt-2 text-sm text-[#0A0A0A]/80 leading-snug">
                    {a.why_buy || "—"}
                  </div>
                  {a.value_estimate && (
                    <div className="mt-3 inline-block bg-[#D4FF00] text-[#0A0A0A] px-2 py-1 text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                      Est: {a.value_estimate}
                    </div>
                  )}
                </div>
                <div className="p-5 bg-[#F4F4F2]">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    OFFER: {data.builders.length} {country?.name} INSTALLERS
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {data.builders.slice(0, 6).map((b) => (
                      <span key={b.id} className="text-[10px] border border-black/25 px-1.5 py-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {b.name.length > 24 ? b.name.slice(0, 22) + "…" : b.name}
                      </span>
                    ))}
                    {data.builders.length > 6 && (
                      <span className="text-[10px] px-1.5 py-0.5 text-[#525252]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        +{data.builders.length - 6} more
                      </span>
                    )}
                  </div>
                  <Link
                    to="/outreach"
                    state={{ associationId: a.id }}
                    data-testid={`draft-email-${a.id}`}
                    className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-3 py-2 text-[10px] tracking-[0.2em] uppercase shadow-[2px_2px_0px_#0A0A0A] hover:bg-[#0038CC] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-colors duration-150"
                    style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                  >
                    DRAFT PITCH EMAIL <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
